import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Search, Filter, Plus, X, Sparkles } from 'lucide-react';
import AILeadScout from '@/components/agent/AILeadScout';
import LeadCard from '@/components/leads/LeadCard';
import LeadForm from '@/components/leads/LeadForm';
import LeadDetailSheet from '@/components/leads/LeadDetailSheet';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/api/AuthContext';
import { toast } from 'sonner';
import { notifyNewLead } from '@/components/notifications/notificationHelper';
import { LEAD_STATUSES, normalizeLeadStatus } from '@/lib/leadStatus';

const STATUS_ORDER = Object.fromEntries(LEAD_STATUSES.map((s, i) => [s, i]));

export default function AgentLeads() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [brancheFilter, setBrancheFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showScout, setShowScout] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['agent-leads', user?.email],
    queryFn: async () => {
      const agentLeads = await base44.entities.Lead.filter({ assigned_agent: user?.email }, 'name');
      return agentLeads.filter(lead =>
        !lead.deactivated_by?.includes(user?.email)
      );
    },
    enabled: !!user?.email,
  });

  const handleCreateLead = async (formData) => {
    setIsSaving(true);
    try {
      const lead = await base44.entities.Lead.create({
        ...formData,
        assigned_agent: user?.email
      });

      // Create initial "Besuchen" task
      if (lead) {
        try {
          await base44.entities.Task.create({
            lead_id: lead.id,
            lead_name: formData.name,
            lead_address: [formData.strasse, formData.plz, formData.ort].filter(Boolean).join(', '),
            // Vertriebsprozess Schritt 1: neuer Kontakt startet mit "Erstbesuch".
            // Nach Abschluss → Status automatisch "Interessenten".
            task_type: 'Erstbesuch',
            typ: 'Erstbesuch',
            due_date: new Date().toISOString().split('T')[0],
            status: 'Offen',
            info_block: 'Erstbesuch — Erstkontakt herstellen',
            assigned_agent: user?.email
          });
        } catch (e) {
          console.warn('Task creation failed:', e);
        }
      }

      // Refresh list, close form, show success
      await queryClient.invalidateQueries(['agent-leads']);
      setShowForm(false);
      toast.success('Kontakt erstellt');
      // Notify admin about new lead
      if (lead) {
        notifyNewLead(lead, user.email, user.name);
      }
    } catch (error) {
      console.error('Lead creation error:', error);
      toast.error('Fehler beim Erstellen: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setDetailOpen(true);
  };

  // Filter and group leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.ort?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || normalizeLeadStatus(lead.status) === statusFilter;
    const matchesBranche = brancheFilter === 'all' || lead.branche === brancheFilter;
    return matchesSearch && matchesStatus && matchesBranche;
  });

  // Group by status and sort
  const groupedLeads = filteredLeads.reduce((acc, lead) => {
    const status = normalizeLeadStatus(lead.status) || 'Potenzieller Interessent';
    if (!acc[status]) acc[status] = [];
    acc[status].push(lead);
    return acc;
  }, {});

  // Sort groups and leads within groups
  const sortedGroups = Object.entries(groupedLeads)
    .sort(([a], [b]) => STATUS_ORDER[a] - STATUS_ORDER[b])
    .map(([status, items]) => ({
      status,
      leads: items.sort((a, b) => a.name.localeCompare(b.name))
    }));

  const uniqueBranchen = [...new Set(leads.map(l => l.branche).filter(Boolean))];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-brand-950 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <PageHeader
                title="Kontakte"
                subtitle={<>{leads.length} gesamt</>}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowScout(true)} size="sm" variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50">
                <Sparkles className="w-4 h-4 mr-1.5" />
                AI Scout
              </Button>
              <Button onClick={() => setShowForm(true)} size="sm">
                <Plus className="w-4 h-4 mr-1.5" />
                Neu
              </Button>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  {LEAD_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={brancheFilter} onValueChange={setBrancheFilter}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Branche" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Branchen</SelectItem>
                  {uniqueBranchen.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Lead List */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Card key={i} className="p-4">
                <Skeleton className="h-6 w-1/3 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </Card>
            ))}
          </div>
        ) : sortedGroups.length > 0 ? (
          <div className="space-y-6">
            {sortedGroups.map(({ status, leads: groupLeads }) => (
              <div key={status}>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  {status}
                  <span className="text-xs bg-slate-200 px-2 py-0.5 rounded-full">
                    {groupLeads.length}
                  </span>
                </h2>
                <div className="space-y-2">
                  {groupLeads.map(lead => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      onClick={handleLeadClick}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-600 font-medium">Keine Kontakte gefunden</p>
            <p className="text-sm text-slate-400">
              {searchTerm || statusFilter !== 'all' || brancheFilter !== 'all'
                ? 'Filter anpassen oder neue Kontakte anlegen'
                : 'Erstellen Sie Ihren ersten Kontakt'}
            </p>
          </Card>
        )}
      </div>

      {/* AI Scout Sheet */}
      <Sheet open={showScout} onOpenChange={setShowScout}>
        <SheetContent side="bottom" className="h-[95vh] overflow-y-auto rounded-t-2xl">
          <SheetHeader className="mb-4">
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI Kontakte Scout
            </SheetTitle>
          </SheetHeader>
          <AILeadScout
            agentEmail={user?.email}
            onComplete={() => {
              setShowScout(false);
              queryClient.invalidateQueries(['agent-leads']);
            }}
          />
        </SheetContent>
      </Sheet>

      {/* Create Form Sheet */}
      <Sheet open={showForm} onOpenChange={setShowForm}>
        <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Neuer Kontakt</SheetTitle>
          </SheetHeader>
          <LeadForm
            onSubmit={handleCreateLead}
            onCancel={() => setShowForm(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Detail Sheet */}
      {selectedLead && (
        <LeadDetailSheet
          lead={selectedLead}
          open={detailOpen}
          onOpenChange={setDetailOpen}
          agentEmail={user?.email}
        />
      )}
    </div>
  );
}
